/**
 * Avatars animés des comptes qui ont ENREGISTRÉ les voix d'une animation
 * (migration 0675) : prénom + 8 images (0 = bouche fermée) lus par la RPC
 * publique `get_speaker_avatars` (anon compris : page publique, capture vidéo),
 * images servies par le bucket PUBLIC `avatar-sprites`.
 *
 * Écriture (profil admin) : `saveMySpeakerSprites` dépose les images dans
 * `<compte>/<n>-<empreinte>.<ext>` (une nouvelle image = un nouveau chemin :
 * jamais de cache périmé) puis enregistre la ligne `profile_avatar_sprites`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import type { SupabaseClient } from '@supabase/supabase-js';
import { documentSpeakers, SPEAKER_FRAME_COUNT } from '@jeraisonne/animation';
import type { SpeakerAvatars } from '@jeraisonne/ui/components/animations/speaker-avatar';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export const AVATAR_SPRITES_BUCKET = 'avatar-sprites';
/** 1 Mo par image : même limite que le bucket. */
export const SPRITE_MAX_BYTES = 1024 * 1024;
export const SPRITE_MIME_TYPES = ['image/png', 'image/webp'] as const;

const rowSchema = z.object({
  id: z.string(),
  first_name: z.string().nullable(),
  frames: z.array(z.string()),
});

export function spriteUrl(path: string): string {
  return db.storage.from(AVATAR_SPRITES_BUCKET).getPublicUrl(path).data.publicUrl;
}

/** Lignes de la RPC → avatars utilisables (8 images exactement). */
export function toSpeakerAvatars(rows: unknown): SpeakerAvatars {
  const parsed = z.array(rowSchema).safeParse(rows);
  if (!parsed.success) return {};
  const out: Record<string, SpeakerAvatars[string]> = {};
  for (const r of parsed.data) {
    if (r.frames.length !== SPEAKER_FRAME_COUNT) continue;
    out[r.id] = { firstName: r.first_name, frames: r.frames.map(spriteUrl) };
  }
  return out;
}

/** Avatars des comptes `ids` (vide : aucune requête). */
export async function fetchSpeakerAvatars(ids: readonly string[]): Promise<SpeakerAvatars> {
  if (ids.length === 0) return {};
  const { data, error } = await db.rpc('get_speaker_avatars', { p_ids: [...ids] });
  if (error) throw error;
  return toSpeakerAvatars(data);
}

/** Avatars des comptes qui ont enregistré les voix de `doc` ; un échec = pas d'avatar. */
export function useSpeakerAvatars(doc: unknown): SpeakerAvatars | null {
  const ids = doc && typeof doc === 'object' ? documentSpeakers(doc as never) : [];
  const key = ids.join(',');
  const q = useQuery({
    queryKey: ['speaker-avatars', key],
    queryFn: () => fetchSpeakerAvatars(ids),
    enabled: ids.length > 0,
    staleTime: 10 * 60_000,
    retry: 1,
  });
  return q.data ?? null;
}

const voiceProfileSchema = z.array(
  z.object({
    id: z.string(),
    display_name: z.string().nullable(),
    avatar_url: z.string().nullable(),
  }),
);

export type VoiceSpeakerProfiles = Readonly<
  Record<string, { displayName: string | null; avatarUrl: string | null }>
>;

/**
 * Nom et photo des comptes qui ont enregistré les voix (`speakerId`), pour
 * l'admin (lecture de tous les profils, 0347) ; un échec = pas de photo.
 */
export function useVoiceSpeakerProfiles(ids: readonly string[]): VoiceSpeakerProfiles {
  const sorted = [...new Set(ids)].sort();
  const q = useQuery({
    queryKey: ['voice-speaker-profiles', sorted.join(',')],
    queryFn: async () => {
      const { data, error } = await db
        .from('profiles')
        .select('id, display_name, avatar_url')
        .in('id', sorted);
      if (error) throw error;
      const parsed = voiceProfileSchema.safeParse(data);
      const out: Record<string, { displayName: string | null; avatarUrl: string | null }> = {};
      if (parsed.success)
        for (const p of parsed.data)
          out[p.id] = { displayName: p.display_name, avatarUrl: p.avatar_url };
      return out;
    },
    enabled: sorted.length > 0,
    staleTime: 10 * 60_000,
    retry: 1,
  });
  return q.data ?? {};
}

// ---------------------------------------------------------- profil admin

/** Mes sprites (chemins dans le bucket), sinon null. */
export function useMySpeakerSprites(userId: string | null | undefined) {
  return useQuery({
    queryKey: ['my-speaker-sprites', userId],
    enabled: !!userId,
    queryFn: async (): Promise<string[] | null> => {
      const { data, error } = await db
        .from('profile_avatar_sprites')
        .select('frames')
        .eq('user_id', userId!)
        .maybeSingle();
      if (error) throw error;
      const frames = (data as { frames?: unknown } | null)?.frames;
      return Array.isArray(frames) ? (frames as string[]) : null;
    },
  });
}

/** Empreinte courte d'un fichier (16 caractères hex). */
export async function fileHash(file: Blob): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16);
}

/** Pourquoi un fichier n'est pas accepté, sinon null. */
export function spriteFileIssue(file: { type: string; size: number }): string | null {
  if (!(SPRITE_MIME_TYPES as readonly string[]).includes(file.type))
    return 'Image PNG ou WebP seulement.';
  if (file.size > SPRITE_MAX_BYTES) return 'Image trop lourde (1 Mo au plus).';
  return null;
}

export interface SpriteSlot {
  /** chemin déjà enregistré */
  path: string | null;
  /** nouvelle image choisie (remplace `path`) */
  file: File | null;
}

/** Dépose les nouvelles images puis enregistre les 8 chemins. */
export function useSaveMySpeakerSprites(userId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slots: readonly SpriteSlot[]): Promise<string[]> => {
      if (!userId) throw new Error('Connecte-toi d’abord.');
      if (slots.length !== SPEAKER_FRAME_COUNT) throw new Error('Il faut 8 images.');
      const frames: string[] = [];
      for (let i = 0; i < slots.length; i++) {
        const s = slots[i]!;
        if (s.file) {
          const issue = spriteFileIssue(s.file);
          if (issue) throw new Error(`Image ${i + 1} : ${issue}`);
          const ext = s.file.type === 'image/webp' ? 'webp' : 'png';
          const path = `${userId}/${i + 1}-${await fileHash(s.file)}.${ext}`;
          const { error } = await db.storage
            .from(AVATAR_SPRITES_BUCKET)
            .upload(path, s.file, { contentType: s.file.type, upsert: true });
          if (error) throw new Error(`Image ${i + 1} : ${error.message}`);
          frames.push(path);
        } else if (s.path) frames.push(s.path);
        else throw new Error(`Il manque l’image ${i + 1}.`);
      }
      const { error } = await db
        .from('profile_avatar_sprites')
        .upsert({ user_id: userId, frames }, { onConflict: 'user_id' });
      if (error) throw error;
      return frames;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['my-speaker-sprites', userId] });
      await qc.invalidateQueries({ queryKey: ['speaker-avatars'] });
    },
  });
}
